import React from 'react';
import { DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, CurrencyDollarIcon, TruckIcon, CubeIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import type { XmlCte } from '../../types/cte';
import { useBaixarXmlCte, useGerarDacteCte } from '../../hooks/useCte';
import { money, formatCnpj, formatTipoFrete, formatDateTime } from '../../utils/format';

interface CteListProps {
  xmlCtes: XmlCte[];
  onSelect: (cte: XmlCte) => void;
  isLoading?: boolean;
}

const CteList: React.FC<CteListProps> = ({
  xmlCtes,
  onSelect,
  isLoading
}) => {
  const baixarXmlMutation = useBaixarXmlCte();
  const gerarDacteMutation = useGerarDacteCte();


  const handleBaixarXml = (e: React.MouseEvent, cteId: number) => {
    e.stopPropagation();
    baixarXmlMutation.mutate(cteId);
  };

  const handleGerarDacte = (e: React.MouseEvent, cteId: number) => {
    e.stopPropagation();
    gerarDacteMutation.mutate(cteId);
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
          <span className="ml-3 text-tx-2">Carregando CTes...</span>
        </div>
      </div>
    );
  }

  if (!xmlCtes || xmlCtes.length === 0) {
    return (
      <div className="bg-surface rounded-iris-lg shadow-sm border border-line p-8">
        <div className="text-center">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-tx-3" />
          <h3 className="mt-4 text-lg font-semibold text-tx">
            Nenhum CTe encontrado
          </h3>
          <p className="mt-2 text-sm text-tx-2">
            Não há CTes para exibir com os filtros aplicados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-iris-lg shadow-sm border border-line overflow-hidden">
      {/* Header para desktop */}
      <div className="hidden lg:block bg-surface-2 px-6 py-3 border-b border-line">
        <div className="grid grid-cols-12 gap-2 text-sm font-medium text-tx-3">
          <div className="col-span-1">CTe</div>
          <div className="col-span-2">Emitente</div>
          <div className="col-span-2">Remetente</div>
          <div className="col-span-2">Destinatário</div>
          <div className="col-span-2">Data/Valor</div>
          <div className="col-span-1">Frete</div>
          <div className="col-span-2">Ações</div>
        </div>
      </div>

      {/* Lista de CTes */}
      <div className="divide-y divide-line">
        {xmlCtes.map((cte) => (
          <div
            key={cte.id}
            onClick={() => onSelect(cte)}
            className="p-4 lg:p-6 hover:bg-surface-2 cursor-pointer transition-colors"
          >
            {/* Layout Mobile */}
            <div className="lg:hidden space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-tx">
                      CTe {cte.numero || 'S/N'}
                    </span>
                    {cte.serie && (
                      <span className="text-sm text-tx-3">
                        Série {cte.serie}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-tx-2 mt-1">
                    NSU: {cte.nsu}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-tx">
                  <BuildingOffice2Icon className="h-4 w-4 text-tx-3 flex-shrink-0" />
                  <span className="font-medium truncate">
                    {cte.emitenteNome || 'Emitente não informado'}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-tx-2">
                  <TruckIcon className="h-4 w-4 text-tx-3 flex-shrink-0" />
                  <span className="truncate">
                    {cte.remetenteNome || 'Remetente não informado'}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm text-tx-2">
                  <CubeIcon className="h-4 w-4 text-tx-3 flex-shrink-0" />
                  <span className="truncate">
                    {cte.destinatarioNome || 'Destinatário não informado'}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-tx-2">
                      <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{formatDateTime(cte.dataEmissao)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-ok font-medium">
                      <CurrencyDollarIcon className="h-4 w-4 flex-shrink-0" />
                      <span>{money(cte.valorTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Informações de Frete - Mobile */}
                <div className="flex items-center justify-between pt-2 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-tx-2">Frete:</span>
                    <span className="font-medium text-tx">{money(cte.valorFrete)}</span>
                  </div>
                  {cte.tipoPagamento && (
                    <div className="flex items-center gap-1">
                      <CreditCardIcon className="h-4 w-4 text-tx-3" />
                      <span className={`font-medium ${formatTipoFrete(cte.tipoPagamento).color}`}>
                        {formatTipoFrete(cte.tipoPagamento).label}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Ações Mobile */}
              <div className="flex gap-2 pt-2 border-t border-line">
                <button
                  onClick={(e) => handleBaixarXml(e, cte.id)}
                  disabled={baixarXmlMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-ok bg-ok-soft rounded-iris hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span className="whitespace-nowrap">Baixar XML</span>
                </button>
                <button
                  onClick={(e) => handleGerarDacte(e, cte.id)}
                  disabled={gerarDacteMutation.isPending}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-danger bg-danger-soft rounded-iris hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  <span className="whitespace-nowrap">Gerar DACTE</span>
                </button>
              </div>
            </div>

            {/* Layout Desktop */}
            <div className="hidden lg:grid grid-cols-12 gap-2 items-center">
              <div className="col-span-1">
                <span className="font-semibold text-tx">
                  {cte.numero || 'S/N'}
                </span>
                {cte.serie && (
                  <p className="text-xs text-tx-3">Série {cte.serie}</p>
                )}
              </div>

              <div className="col-span-2">
                <div>
                  <p className="font-medium text-tx truncate">
                    {cte.emitenteNome || 'Nome não informado'}
                  </p>
                  <p className="text-sm text-tx-3 truncate">
                    {formatCnpj(cte.emitenteCnpj)}
                  </p>
                </div>
              </div>

              <div className="col-span-2">
                <div>
                  <p className="text-sm text-tx truncate">
                    {cte.remetenteNome || 'Não informado'}
                  </p>
                  <p className="text-xs text-tx-3 truncate">
                    {formatCnpj(cte.remetenteCnpj)}
                  </p>
                </div>
              </div>

              <div className="col-span-2">
                <div>
                  <p className="text-sm text-tx truncate">
                    {cte.destinatarioNome || 'Não informado'}
                  </p>
                  <p className="text-xs text-tx-3 truncate">
                    {formatCnpj(cte.destinatarioCnpj)}
                  </p>
                </div>
              </div>

              <div className="col-span-2">
                <div>
                  <p className="text-sm font-medium text-tx">
                    {money(cte.valorTotal)}
                  </p>
                  <p className="text-xs text-tx-3">
                    {formatDateTime(cte.dataEmissao)}
                  </p>
                </div>
              </div>

              <div className="col-span-1">
                <div>
                  <p className="text-sm font-medium text-tx">
                    {money(cte.valorFrete)}
                  </p>
                  <p className={`text-xs font-medium ${formatTipoFrete(cte.tipoPagamento).color}`}>
                    {formatTipoFrete(cte.tipoPagamento).label}
                  </p>
                </div>
              </div>

              <div className="col-span-2">
                <div className="flex flex-col sm:flex-row gap-1">
                  <button
                    onClick={(e) => handleBaixarXml(e, cte.id)}
                    disabled={baixarXmlMutation.isPending}
                    className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-ok bg-ok-soft rounded-iris-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                    title="Baixar XML"
                  >
                    <ArrowDownTrayIcon className="h-3 w-3" />
                    XML
                  </button>
                  <button
                    onClick={(e) => handleGerarDacte(e, cte.id)}
                    disabled={gerarDacteMutation.isPending}
                    className="inline-flex items-center justify-center gap-1 px-2 py-1 text-xs font-medium text-danger bg-danger-soft rounded-iris-sm hover:opacity-90 transition-opacity disabled:opacity-50"
                    title="Gerar DACTE PDF"
                  >
                    <DocumentTextIcon className="h-3 w-3" />
                    DACTE
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CteList;